import React, { useRef, useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import { exportChart } from '../../../utilities/ExportChart';
import EmptyState from '../../../components/empty-state/EmptyState';
import FullScreenIcon from '../../common/FullScreenIcon/FullScreenIcon';
import DownloadChart from '../../../components/time-series-chart/download/downloadChart';
import { BoxPlotChartStatistic } from '../RunlifeDarshboardSlice';
import './RunlifeBarChart.scss';

// Constants for chart configuration
const CHART_CONFIG = {
  COLORS: {
    PRIMARY: '#60BFDA',
    WHITE: '#FFFFFF',
    BLACK: '#003747',
    BACKGROUND: '#122031',
    AXIS: '#4A5463',
    GRID: '#616a77',
    TOOLTIP_BG: '#001023',
    TOOLTIP_BORDER: '#4A5463',
  },
  DIMENSIONS: {
    BOX_WIDTH: 40,
    CAP_WIDTH: 20,
    CAP_HEIGHT: 2,
    MEDIAN_WIDTH: 10,
    MEDIAN_HEIGHT: 1,
    OUTLIER_RADIUS: 3,
  },
  AXIS: {
    MIN_GRID_DISTANCE_X: 50,
    MIN_GRID_DISTANCE_Y: 30,
    SCALE_FACTOR: 200,
    MIN_SCALE_FACTOR: 100,
  },
} as const;

interface RunlifeStatsChartProps {
  chartData: BoxPlotChartStatistic[];
  widgetHeader: string;
  chartLabel: string;
}

interface BoxData {
  X: string;
  FirstQuartile: number;
  ThirdQuartile: number;
  Median: number;
  Mean: number;
  Minimum: number;
  Maximum: number;
  outlierCount: number;
}

interface WhiskerData {
  X: string;
  whiskerType: 'lower' | 'upper';
  yStart: number;
  yEnd: number;
}

interface CapData {
  X: string;
  value: number;
}

interface OutlierData {
  X: string;
  outlierValue: number;
}

interface TransformedData {
  boxData: BoxData[];
  whiskerData: WhiskerData[];
  capData: CapData[];
  outlierData: OutlierData[];
}

interface StyleConfig {
  commonAxis: {
    strokeWidth: number;
    strokeOpacity: number;
    stroke: am5.Color;
  };
  grid: {
    stroke: am5.Color;
    strokeWidth: number;
    strokeOpacity: number;
    visible: boolean;
    strokeDasharray: number[];
  };
  labels: {
    fontSize: number;
  };
  tooltip: {
    background: am5.Color;
    text: am5.Color;
  };
}

// Helper functions for creating chart elements
const createTooltip = (root: am5.Root, styleConfig: StyleConfig, isOutlier = false): am5.Tooltip => {
  const labelText = isOutlier
    ? `Install #{categoryX}\nOutlier: {valueY} days`
    : `Max = {Maximum}
Min = {Minimum}
Median = {Median}
Mean = {Mean}
Q1 = {openValueY}
Q3 = {valueY}
days`;

  const tooltip = am5.Tooltip.new(root, {
    labelText,
    getFillFromSprite: false,
    autoTextColor: false,
  });

  const background = isOutlier ? am5.color(CHART_CONFIG.COLORS.TOOLTIP_BG) : styleConfig.tooltip.background;
  const stroke = isOutlier ? am5.color(CHART_CONFIG.COLORS.TOOLTIP_BORDER) : styleConfig.tooltip.background;

  tooltip.get('background')?.setAll({
    fill: background,
    fillOpacity: isOutlier ? 0.95 : 1,
    stroke,
    strokeWidth: isOutlier ? 1 : 0,
  });

  tooltip.label.setAll({
    fill: am5.color(CHART_CONFIG.COLORS.WHITE),
    ...styleConfig.labels,
    textAlign: isOutlier ? 'left' : undefined,
    paddingTop: isOutlier ? 8 : undefined,
    paddingBottom: isOutlier ? 8 : undefined,
    paddingLeft: isOutlier ? 12 : undefined,
    paddingRight: isOutlier ? 12 : undefined,
  });

  return tooltip;
};

const createBulletSprite = (root: am5.Root, type: 'cap' | 'median' | 'outlier') => {
  switch (type) {
    case 'cap':
      return am5.Rectangle.new(root, {
        width: CHART_CONFIG.DIMENSIONS.CAP_WIDTH,
        height: CHART_CONFIG.DIMENSIONS.CAP_HEIGHT,
        fill: am5.color(CHART_CONFIG.COLORS.PRIMARY),
        stroke: am5.color(CHART_CONFIG.COLORS.PRIMARY),
        strokeWidth: 1,
        fillOpacity: 1,
        strokeOpacity: 1,
        centerX: am5.percent(50),
        centerY: am5.percent(50),
        x: am5.percent(50),
        y: am5.percent(50),
      });

    case 'median':
      return am5.Rectangle.new(root, {
        width: CHART_CONFIG.DIMENSIONS.MEDIAN_WIDTH,
        height: CHART_CONFIG.DIMENSIONS.MEDIAN_HEIGHT,
        fill: am5.color(CHART_CONFIG.COLORS.BLACK),
        stroke: am5.color(CHART_CONFIG.COLORS.BLACK),
        strokeWidth: 1,
        fillOpacity: 1,
        strokeOpacity: 1,
        centerX: am5.percent(50),
        centerY: am5.percent(50),
        x: am5.percent(50),
        y: am5.percent(50),
      });

    case 'outlier':
      return am5.Circle.new(root, {
        radius: CHART_CONFIG.DIMENSIONS.OUTLIER_RADIUS,
        fill: am5.color(CHART_CONFIG.COLORS.PRIMARY),
        stroke: am5.color(CHART_CONFIG.COLORS.PRIMARY),
        strokeWidth: 0,
      });

    default:
      throw new Error(`Unknown bullet type: ${type}`);
  }
};

const createLineSeries = (
  root: am5.Root,
  chart: am5xy.XYChart,
  xAxis: am5xy.CategoryAxis<am5xy.AxisRenderer>,
  yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>,
  name: string,
  valueYField: string,
  bulletType?: 'cap' | 'median' | 'outlier',
): am5xy.LineSeries => {
  const series = chart.series.push(
    am5xy.LineSeries.new(root, {
      name,
      xAxis,
      yAxis,
      valueYField,
      categoryXField: 'X',
    }),
  );

  if (bulletType) {
    series.bullets.push(() => am5.Bullet.new(root, { sprite: createBulletSprite(root, bulletType) }));
  }

  series.strokes.template.set('visible', false);
  return series;
};

const createWhiskerSeries = (
  root: am5.Root,
  chart: am5xy.XYChart,
  xAxis: am5xy.CategoryAxis<am5xy.AxisRenderer>,
  yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>,
  whiskerData: WhiskerData[],
): am5xy.LineSeries[] => {
  return whiskerData.map((whisker) => {
    const whiskerLine = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: 'WhiskerLine',
        xAxis,
        yAxis,
        valueYField: 'value',
        categoryXField: 'X',
      }),
    );

    whiskerLine.set('fill', am5.color(CHART_CONFIG.COLORS.PRIMARY));
    whiskerLine.set('stroke', am5.color(CHART_CONFIG.COLORS.PRIMARY));

    whiskerLine.strokes.template.setAll({
      stroke: am5.color(CHART_CONFIG.COLORS.PRIMARY),
      strokeWidth: 2,
    });

    whiskerLine.data.setAll([
      { X: whisker.X, value: whisker.yStart },
      { X: whisker.X, value: whisker.yEnd },
    ]);

    return whiskerLine;
  });
};

const RunlifeStatsChart: React.FC<RunlifeStatsChartProps> = ({ chartData, widgetHeader = '', chartLabel = '' }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const am5RootRef = useRef<am5.Root | null>(null);

  const hasData = useMemo(() => Array.isArray(chartData) && chartData.length > 0, [chartData]);

  // Memoize axis bounds calculation
  const { maxValue, minValue } = useMemo(() => {
    if (!hasData) return { maxValue: 1000, minValue: 0 };

    let maxVal = -Infinity;
    let minVal = Infinity;

    chartData.forEach((item) => {
      const values = [item.Maximum, item.Minimum, ...item.Outliers];
      maxVal = Math.max(maxVal, ...values);
      minVal = Math.min(minVal, ...values);
    });

    return {
      maxValue: Math.ceil(maxVal / CHART_CONFIG.AXIS.SCALE_FACTOR) * CHART_CONFIG.AXIS.SCALE_FACTOR,
      minValue: Math.max(
        0,
        Math.floor(minVal / CHART_CONFIG.AXIS.MIN_SCALE_FACTOR) * CHART_CONFIG.AXIS.MIN_SCALE_FACTOR,
      ),
    };
  }, [chartData, hasData]);

  // Memoize transformed chart data
  const transformedData: TransformedData = useMemo(() => {
    if (!hasData) return { boxData: [], whiskerData: [], capData: [], outlierData: [] };

    const boxData: BoxData[] = [];
    const whiskerData: WhiskerData[] = [];
    const capData: CapData[] = [];
    const outlierData: OutlierData[] = [];

    chartData.forEach((item) => {
      const x = String(item.X);

      // Box data
      boxData.push({
        X: x,
        FirstQuartile: item.FirstQuartile,
        ThirdQuartile: item.ThirdQuartile,
        Median: item.Median,
        Mean: item.Mean,
        Minimum: item.Minimum,
        Maximum: item.Maximum,
        outlierCount: item.Outliers.length,
      });

      // Cap data
      capData.push({ X: x, value: item.Minimum }, { X: x, value: item.Maximum });

      // Whisker data for vertical lines
      if (item.Minimum != null && item.FirstQuartile != null && item.FirstQuartile !== item.Minimum) {
        whiskerData.push({
          X: x,
          whiskerType: 'lower',
          yStart: item.FirstQuartile,
          yEnd: item.Minimum,
        });
      }

      if (item.ThirdQuartile != null && item.Maximum != null && item.ThirdQuartile !== item.Maximum) {
        whiskerData.push({
          X: x,
          whiskerType: 'upper',
          yStart: item.ThirdQuartile,
          yEnd: item.Maximum,
        });
      }

      // Outlier data
      item.Outliers.forEach((outlier) => {
        outlierData.push({ X: x, outlierValue: outlier });
      });
    });

    return { boxData, whiskerData, capData, outlierData };
  }, [chartData, hasData]);

  // Common style configurations
  const styleConfig: StyleConfig = useMemo(
    () => ({
      commonAxis: {
        strokeWidth: 1,
        strokeOpacity: 1,
        stroke: am5.color(CHART_CONFIG.COLORS.AXIS),
      },
      grid: {
        stroke: am5.color(CHART_CONFIG.COLORS.GRID),
        strokeWidth: 1,
        strokeOpacity: 0.3,
        visible: true,
        strokeDasharray: [2, 2],
      },
      labels: {
        fontSize: 12,
      },
      tooltip: {
        background: am5.color(CHART_CONFIG.COLORS.TOOLTIP_BG),
        text: am5.color(CHART_CONFIG.COLORS.WHITE),
      },
    }),
    [],
  );

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(console.error);
    } else {
      document.exitFullscreen();
    }
  }, []);

  const handleChartDownload = useCallback(
    async (option: string) => {
      const element = document.getElementById('runlife-stats-chart-content');
      if (!element) return;
      const type = option === 'Download as PDF' ? 'PDF' : 'JPG';
      await exportChart(element, type, widgetHeader, undefined, undefined, widgetHeader);
    },
    [widgetHeader],
  );

  const createAxis = useCallback(
    (root: am5.Root, chart: am5xy.XYChart) => {
      const commonAxisConfig = styleConfig.commonAxis;

      // X-axis (Install Numbers)
      const xAxisRenderer = am5xy.AxisRendererX.new(root, {
        ...commonAxisConfig,
        minGridDistance: CHART_CONFIG.AXIS.MIN_GRID_DISTANCE_X,
      });
      const xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, { categoryField: 'X', renderer: xAxisRenderer }));

      xAxisRenderer.labels.template.setAll(styleConfig.labels);

      xAxis.children.moveValue(
        am5.Label.new(root, {
          x: am5.percent(50),
          centerX: am5.percent(50),
          ...styleConfig.labels,
          marginTop: 10,
        }),
        xAxis.children.length - 1,
      );

      // Y-axis (Runlife Days)
      const yAxisRenderer = am5xy.AxisRendererY.new(root, {
        ...commonAxisConfig,
        minGridDistance: CHART_CONFIG.AXIS.MIN_GRID_DISTANCE_Y,
      });
      const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: yAxisRenderer,
          min: minValue,
          max: maxValue,
        }),
      );

      yAxisRenderer.labels.template.setAll(styleConfig.labels);

      yAxis.children.unshift(
        am5.Label.new(root, {
          rotation: -90,
          y: am5.percent(50),
          centerX: am5.percent(50),
          textAlign: 'center',
          ...styleConfig.labels,
          marginRight: 15,
        }),
      );

      xAxis.get('renderer').grid.template.setAll(styleConfig.grid);
      yAxis.get('renderer').grid.template.setAll(styleConfig.grid);

      xAxis.data.setAll(transformedData.boxData.map((item) => ({ X: item.X })));
      return { xAxis, yAxis };
    },
    [styleConfig, minValue, maxValue, transformedData.boxData],
  );

  const createBoxPlotElements = useCallback(
    (
      root: am5.Root,
      chart: am5xy.XYChart,
      xAxis: am5xy.CategoryAxis<am5xy.AxisRenderer>,
      yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>,
    ) => {
      const { boxData, whiskerData, capData, outlierData } = transformedData;

      // Main box (Q1 to Q3)
      const boxSeries = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: 'Quartile Box',
          xAxis,
          yAxis,
          valueYField: 'ThirdQuartile',
          openValueYField: 'FirstQuartile',
          categoryXField: 'X',
          fill: am5.color(CHART_CONFIG.COLORS.PRIMARY),
          stroke: am5.color(CHART_CONFIG.COLORS.PRIMARY),
        }),
      );

      boxSeries.columns.template.setAll({
        width: am5.percent(CHART_CONFIG.DIMENSIONS.BOX_WIDTH),
        strokeWidth: 1,
        stroke: am5.color(CHART_CONFIG.COLORS.PRIMARY),
        fill: am5.color(CHART_CONFIG.COLORS.PRIMARY),
        fillOpacity: 0.7,
        cornerRadiusTL: 2,
        cornerRadiusTR: 2,
        cornerRadiusBL: 2,
        cornerRadiusBR: 2,
      });

      const whiskerLines = createWhiskerSeries(root, chart, xAxis, yAxis, whiskerData);

      const capSeries = createLineSeries(root, chart, xAxis, yAxis, 'Whisker Caps', 'value', 'cap');

      const medianSeries = createLineSeries(root, chart, xAxis, yAxis, 'Median', 'Median', 'median');

      const meanSeries = createLineSeries(root, chart, xAxis, yAxis, 'Mean', 'Mean', 'median');

      boxSeries.set('tooltip', createTooltip(root, styleConfig));

      boxSeries.data.setAll(boxData);
      capSeries.data.setAll(capData);
      medianSeries.data.setAll(boxData);
      meanSeries.data.setAll(boxData);

      let outlierSeries: am5xy.LineSeries | null = null;
      if (outlierData.length > 0) {
        outlierSeries = createLineSeries(root, chart, xAxis, yAxis, 'Outliers', 'outlierValue', 'outlier');
        outlierSeries.data.setAll(outlierData);
        outlierSeries.set('tooltip', createTooltip(root, styleConfig, true));
      }

      return { boxSeries, whiskerLines, medianSeries, meanSeries, capSeries, outlierSeries };
    },
    [transformedData, styleConfig],
  );

  const createLegend = useCallback(
    (root: am5.Root, chart: am5xy.XYChart, allSeries: (am5xy.XYSeries | null)[]) => {
      const legend = chart.children.push(
        am5.Legend.new(root, {
          centerX: am5.percent(50),
          x: am5.percent(50),
          marginTop: 15,
          marginBottom: 5,
        }),
      );

      const legendConfig = {
        ...styleConfig.labels,
        fill: am5.color(CHART_CONFIG.COLORS.WHITE),
      };
      legend.labels.template.setAll(legendConfig);
      legend.valueLabels.template.setAll(legendConfig);
      legend.markers.template.setAll({ width: 12, height: 12 });

      legend.markerRectangles.template.setAll({
        cornerRadiusTL: 50,
        cornerRadiusTR: 50,
        cornerRadiusBL: 50,
        cornerRadiusBR: 50,
        strokeWidth: 2,
        fill: am5.color(CHART_CONFIG.COLORS.PRIMARY),
        fillOpacity: 1,
      });

      legend.labels.template.adapters.add('text', (_, target) => (target.dataItem?.dataContext as any)?.name ?? '');

      const markerTemplate = legend.markerRectangles.template;
      markerTemplate.adapters.add('fill', (fill, target) => (target.dataItem?.dataContext as any)?.color ?? fill);
      markerTemplate.adapters.add('fillOpacity', (_, target) =>
        (target.dataItem?.dataContext as any)?.visible === false ? 0 : 1,
      );
      markerTemplate.adapters.add('stroke', (stroke, target) => (target.dataItem?.dataContext as any)?.color ?? stroke);

      legend.itemContainers.template.events.on('click', (ev: any) => {
        const legendData = ev.target.dataItem?.dataContext;
        if (!legendData) return;

        const isVisible = legendData.visible !== false;
        legendData.visible = !isVisible;

        allSeries.forEach((series) => series?.set('visible', !isVisible));

        const legendItem = legend.dataItems.find((item) => item.dataContext === legendData);
        if (legendItem) {
          const marker = legendItem.get('marker')?.children.getIndex(0) as am5.RoundedRectangle;
          marker?.set('fillOpacity', !isVisible ? 1 : 0);
        }
      });

      legend.itemContainers.template.set('cursorOverStyle', 'pointer');

      const legendData = [
        {
          name: 'Runlife',
          color: am5.color(CHART_CONFIG.COLORS.PRIMARY),
          visible: true,
        },
      ];
      legend.data.setAll(legendData);
    },
    [styleConfig],
  );

  useLayoutEffect(() => {
    if (!chartRef.current || !hasData) return;

    if (am5RootRef.current) {
      am5RootRef.current.dispose();
      am5RootRef.current = null;
    }

    chartRef.current.innerHTML = '';
    const root = am5.Root.new(chartRef.current);
    am5RootRef.current = root;
    root.setThemes([am5themes_Dark.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        layout: root.verticalLayout,
        paddingLeft: 10,
        paddingRight: 30,
        paddingTop: 20,
        paddingBottom: 10,
        background: am5.Rectangle.new(root, {
          fill: am5.color(CHART_CONFIG.COLORS.BACKGROUND),
          fillOpacity: 0,
        }),
      }),
    );

    const { xAxis, yAxis } = createAxis(root, chart);

    const cursor = chart.set(
      'cursor',
      am5xy.XYCursor.new(root, {
        behavior: 'zoomX',
        xAxis: xAxis,
      }),
    );
    cursor.lineX.set('visible', false);
    cursor.lineY.set('visible', false);

    const { boxSeries, whiskerLines, medianSeries, meanSeries, capSeries, outlierSeries } = createBoxPlotElements(
      root,
      chart,
      xAxis,
      yAxis,
    );

    const allSeries = [boxSeries, ...whiskerLines, medianSeries, meanSeries, capSeries, outlierSeries].filter(Boolean);
    createLegend(root, chart, allSeries);

    chart.appear(1000, 100);

    return () => {
      if (am5RootRef.current) {
        am5RootRef.current.dispose();
        am5RootRef.current = null;
      }
    };
  }, [hasData, createAxis, createBoxPlotElements, createLegend]);

  return (
    <div className='runlife-main-container'>
      <section
        ref={containerRef}
        className={`ogl-chart-container runlife-bar-chart-container${isFullscreen ? ' fullscreen' : ''}`}
      >
        <header className='ogl-chart-header'>
          <div className='chart-title'>
            <h2>{widgetHeader}</h2>
          </div>
          <div className='chart-actions'>
            <button
              analytics-id='runlife-stats-chart-fullscreen-btn'
              className='chart-action-button'
              onClick={toggleFullscreen}
            >
              <FullScreenIcon />
            </button>
            <button analytics-id='runlife-stats-chart-download-btn' className='chart-action-button'>
              <DownloadChart onClick={handleChartDownload} canExportJpg={true} canExportPdf={true} />
            </button>
          </div>
        </header>
        <div className='chart-divider'></div>
        <div id='runlife-stats-chart-content'>
          <div className='runlife-bar-chart-label'>{chartLabel ?? ''}</div>
          {hasData ? <div ref={chartRef} className='chart-area' /> : <EmptyState message='No data available' />}
        </div>
      </section>
    </div>
  );
};

export default RunlifeStatsChart;
